import React from 'react';
import {Helmet} from 'react-helmet-async';
import {Link, useOutletContext} from 'react-router-dom';
import GeoPageLayout from '../../components/geo/GeoPageLayout';
import GeoFaqSection from '../../components/geo/GeoFaqSection';
import GeoHubHero from '../../components/geo/GeoHubHero';
import GeoHubRestaurantGrid from '../../components/geo/GeoHubRestaurantGrid';
import GeoHubSeoGuide from '../../components/geo/GeoHubSeoGuide';
import {getGeoCityContent} from '../../data/geoContent.mock';
import {
    buildBreadcrumbListSchema,
    buildFaqPageSchema,
    buildHubRestaurantItemListSchema,
} from '../../data/geoStructuredData';
import {useGeoCityHub} from '../../hooks/useGeoCityHub';
import {
    GeoCityConfig,
    DEFAULT_OG_IMAGE,
    SITE_ORIGIN,
    absoluteUrl,
    geoBestRestaurantsPath,
    geoCityPath,
} from '../../data/geoRoutes';

const CityGeoHub: React.FC = () => {
    const geo = useOutletContext<GeoCityConfig>();
    const {hub, restaurants, snapshotFetchedAt} = useGeoCityHub(geo);
    const content = getGeoCityContent(geo.restaurantsDataKey);
    const canonicalPath = geoCityPath(geo);
    const canonicalUrl = absoluteUrl(canonicalPath);
    const year = new Date().getFullYear();

    const pageTitle = `Where to eat in ${geo.cityName}, ${geo.countryName} (${year}) | Solt`;
    const snapshotLabel = snapshotFetchedAt
        ? new Date(snapshotFetchedAt).toLocaleDateString('en-GB', {
              month: 'long',
              year: 'numeric',
          })
        : null;
    const pageDescription =
        hub?.subtitle ??
        `Where to eat in ${geo.cityName}: neighborhoods, best restaurants, must-try dishes, and real food photos from Solt foodies. Updated ${year}.${
            snapshotLabel ? ` List snapshot: ${snapshotLabel}.` : ''
        }`;

    const faq = hub?.faq ?? content?.faq ?? [];
    const faqSchema = buildFaqPageSchema(faq);

    const restaurantListSchema = buildHubRestaurantItemListSchema(geo.cityName, restaurants);
    const breadcrumbSchema = buildBreadcrumbListSchema([
        {name: 'Solt', item: `${SITE_ORIGIN}/`},
        {name: `Where to eat in ${geo.cityName}`, item: canonicalUrl},
    ]);

    const structuredData = {
        '@context': 'https://schema.org',
        '@graph': [
            {
                '@type': 'WebPage',
                '@id': `${canonicalUrl}#webpage`,
                url: canonicalUrl,
                name: pageTitle,
                description: pageDescription,
                inLanguage: 'en',
                isPartOf: {'@id': `${SITE_ORIGIN}/#website`},
            },
            breadcrumbSchema,
            ...(restaurantListSchema ? [restaurantListSchema] : []),
            ...(faqSchema ? [faqSchema] : []),
        ],
    };

    return (
        <GeoPageLayout geo={geo} breadcrumbLabel={geo.cityName} variant="hub">
            <Helmet>
                <title>{pageTitle}</title>
                <meta name="description" content={pageDescription}/>
                <link rel="canonical" href={canonicalUrl}/>
                <meta property="og:title" content={pageTitle}/>
                <meta property="og:description" content={pageDescription}/>
                <meta property="og:url" content={canonicalUrl}/>
                <meta property="og:type" content="article"/>
                <meta property="og:image" content={DEFAULT_OG_IMAGE}/>
                <meta name="twitter:card" content="summary_large_image"/>
                <meta name="twitter:image" content={DEFAULT_OG_IMAGE}/>
                <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
            </Helmet>

            <article className="geo-article geo-article--hub">
                <GeoHubHero
                    cityName={geo.cityName}
                    subtitle={hub?.subtitle ?? pageDescription}
                    bannerSrc={hub?.bannerSrc}
                />

                {restaurants.length > 0 && (
                    <GeoHubRestaurantGrid cityName={geo.cityName} restaurants={restaurants}/>
                )}

                <nav className="geo-hub-actions" aria-label="Related guides">
                        <Link to={geoBestRestaurantsPath(geo)} className="geo-hub-action-card">
                            <span className="geo-hub-action-card__label">Ranked list</span>
                            <strong>Best restaurants in {geo.cityName}</strong>
                            <span>Top picks for {year} with dish photos on Solt</span>
                        </Link>
                </nav>

                {faq.length > 0 && (
                    <GeoFaqSection cityName={geo.cityName} items={faq} variant="accordion"/>
                )}

                {hub && (
                    <GeoHubSeoGuide
                        cityName={geo.cityName}
                        aboutCity={hub.aboutCity}
                        restaurants={restaurants}
                    />
                )}
            </article>
        </GeoPageLayout>
    );
};

export default CityGeoHub;
